import React, {
  createContext,
  useReducer,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  memo,
  useContext,
} from "react";
import PropTypes from "prop-types";
import CameraCapture from "../../services/camera/CameraCapture";

/**
 * Single shared camera capture modal. Use with CameraTrigger or useCameraCapture().
 * Promise-based openModal, optional replace/queue, state machine, unmount-safe.
 *
 * @typedef {object} CameraCaptureModalConfig
 * @property {string} [inputFileType]
 * @property {boolean} [multiple]
 * @property {number|null} [maxSelect]
 * @property {string} [title]
 * @property {() => React.ReactNode} [note]
 * @property {number} [maxFileSize]
 * @property {boolean} [allowVideo]
 * @property {boolean} [allowedFromGallery] - true = enable gallery button; false/undefined = disable
 * @property {boolean} [centerBoxOverlay] - true = show center guide box for OCR (align content in frame)
 * @property {string} [centerBoxHint] - instruction text shown below the center box when centerBoxOverlay is true
 * @property {number} [centerBoxWidthRatio] - width of center box as fraction 0–1 (default 0.7)
 * @property {number} [centerBoxHeightRatio] - height of center box as fraction 0–1 (default 0.5)
 */

export const CameraCaptureContext = createContext(null);

/** Rejected when user closes the modal without confirming. */
export const CAPTURE_CANCELLED = Symbol("CAPTURE_CANCELLED");

/** Rejected when openModal is called again while already open and replace is false (and queue disabled). */
export const CAPTURE_REPLACED = Symbol("CAPTURE_REPLACED");

const FSM = Object.freeze({
  IDLE: "idle",
  OPEN: "open",
});

const DEFAULT_CONFIG = Object.freeze({
  inputFileType: "png,jpeg,jpg",
  multiple: false,
  maxSelect: null,
  title: undefined,
  note: () => null,
  maxFileSize: 50,
  allowVideo: undefined,
  allowedFromGallery: undefined,
  centerBoxOverlay: undefined,
  centerBoxHint: undefined,
  centerBoxWidthRatio: 0.7,
  centerBoxHeightRatio: 0.5,
});

function reducer(state, action) {
  switch (action.type) {
    case "OPEN":
      return { phase: FSM.OPEN, config: action.payload };
    case "CLOSE":
      return { phase: FSM.IDLE, config: state.config };
    default:
      return state;
  }
}

function validateCameraConfig(config) {
  const out = { ...config };
  if (typeof out.maxFileSize !== "number" || out.maxFileSize <= 0) {
    out.maxFileSize = 50;
  }
  if (out.multiple && (out.maxSelect == null || typeof out.maxSelect !== "number")) {
    out.maxSelect = null;
  }
  return out;
}

function mergeConfig(overrides) {
  if (!overrides || typeof overrides !== "object") return { ...DEFAULT_CONFIG };
  return validateCameraConfig({ ...DEFAULT_CONFIG, ...overrides });
}

const QUEUE_WHEN_REPLACE_FALSE = false;

function CameraCaptureProviderInner({ children }) {
  const [state, dispatch] = useReducer(reducer, { phase: FSM.IDLE, config: DEFAULT_CONFIG });
  const resolveRef = useRef(null);
  const rejectRef = useRef(null);
  const queueRef = useRef([]);
  const phaseRef = useRef(state.phase);
  phaseRef.current = state.phase;

  const isOpen = state.phase === FSM.OPEN;

  useEffect(() => {
    return () => {
      const reject = rejectRef.current;
      if (typeof reject === "function") reject(CAPTURE_CANCELLED);
      resolveRef.current = null;
      rejectRef.current = null;
      queueRef.current.forEach((item) => {
        if (typeof item.reject === "function") item.reject(CAPTURE_CANCELLED);
      });
      queueRef.current = [];
    };
  }, []);

  const processQueue = useCallback(() => {
    const queue = queueRef.current;
    if (queue.length === 0) {
      dispatch({ type: "CLOSE" });
      return;
    }
    const next = queue.shift();
    resolveRef.current = next.resolve;
    rejectRef.current = next.reject;
    dispatch({ type: "OPEN", payload: next.config });
  }, []);

  const closeModal = useCallback(() => {
    const reject = rejectRef.current;
    if (typeof reject === "function") reject(CAPTURE_CANCELLED);
    resolveRef.current = null;
    rejectRef.current = null;
    processQueue();
  }, [processQueue]);

  const handleSelect = useCallback(
    (files) => {
      const resolve = resolveRef.current;
      if (typeof resolve === "function") resolve(files);
      resolveRef.current = null;
      rejectRef.current = null;
      processQueue();
    },
    [processQueue]
  );

  /**
   * @param {CameraCaptureModalConfig} modalConfig
   * @param {{ replace?: boolean, queue?: boolean }} [opts]
   * @returns {Promise<File[]>}
   */
  const openModal = useCallback((modalConfig, opts = {}) => {
    const options = typeof opts === "object" ? opts : {};
    const replace = options.replace !== false;
    const useQueue = options.queue === true || (QUEUE_WHEN_REPLACE_FALSE && !replace);

    const alreadyOpen = phaseRef.current === FSM.OPEN;

    if (alreadyOpen && !replace && !useQueue) {
      return Promise.reject(CAPTURE_REPLACED);
    }

    if (alreadyOpen && replace && !useQueue) {
      const prevReject = rejectRef.current;
      if (typeof prevReject === "function") prevReject(CAPTURE_REPLACED);
    }

    const config = mergeConfig(modalConfig);

    const promise = new Promise((resolve, reject) => {
      if (alreadyOpen && useQueue) {
        queueRef.current.push({ config, resolve, reject });
        return;
      }
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });

    if (alreadyOpen && useQueue) return promise;

    dispatch({ type: "OPEN", payload: config });
    return promise;
  }, []);

  const contextValue = useMemo(
    () => ({ openModal, closeModal, isOpen }),
    [openModal, closeModal, isOpen]
  );

  const modalProps = useMemo(
    () =>
      isOpen
        ? {
            ...state.config,
            open: true,
            onClose: closeModal,
            onSelect: handleSelect,
          }
        : null,
    [isOpen, state.config, closeModal, handleSelect]
  );

  return (
    <CameraCaptureContext.Provider value={contextValue}>
      {children}
      {isOpen && modalProps && <CameraCapture key="camera-capture" {...modalProps} />}
    </CameraCaptureContext.Provider>
  );
}

CameraCaptureProviderInner.displayName = "CameraCaptureProvider";
CameraCaptureProviderInner.propTypes = { children: PropTypes.node };
CameraCaptureProviderInner.defaultProps = { children: null };

export const CameraCaptureProvider = memo(CameraCaptureProviderInner);

export function useCameraCapture() {
  const value = useContext(CameraCaptureContext);
  if (value === null && process.env.NODE_ENV !== "production") {
    console.warn("useCameraCapture must be used within CameraCaptureProvider.");
  }
  return value;
}
